import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/*
 * Page scroll lock while a dialog is open.
 *
 * Desktop keeps the theme's original lock (body { position: fixed } + restore scroll on close).
 * Mobile (<= 749px) uses a touch guard instead: pinning and un-pinning <body> forced a full
 * re-layout of the page at the start AND at the end of every open/close, plus a scroll jump on
 * close. On long pages (the products page) that was the stutter felt right after pressing X in
 * search, the cart or the filters. The guard only blocks drags that would scroll the page; drags
 * inside a scrollable area of the open dialog (results list, cart items) scroll normally.
 *
 * `ps-dialog-settled` is added to the dialog once its opening slide is over. Until then, CSS can
 * keep the content still (e.g. the filter sections appear already open instead of sliding down).
 */
const mobileQuery = window.matchMedia('(max-width: 749px)');
const openTouchLockedDialogs = new Set();
let touchAllowed = false;
const SETTLE_MS = 450;

/** @param {EventTarget | null} target */
function canScrollInsideDialog(target) {
  if (!(target instanceof Element)) return false;

  for (const dialog of openTouchLockedDialogs) {
    if (!dialog.contains(target)) continue;

    for (let node = target; node && node !== dialog.parentElement; node = node.parentElement) {
      if (!(node instanceof HTMLElement)) continue;
      const style = getComputedStyle(node);
      const scrollY = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 1;
      const scrollX = /(auto|scroll)/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 1;
      if (scrollY || scrollX) return true;
    }
  }

  return false;
}

/** @param {TouchEvent} event */
function onTouchStart(event) {
  /* Decide once per gesture, not on every touchmove. */
  touchAllowed = canScrollInsideDialog(event.target);
}

/** @param {TouchEvent} event */
function onTouchMove(event) {
  if (touchAllowed) return;
  if (event.cancelable) event.preventDefault();
}

/** @param {HTMLDialogElement} dialog */
function lockTouch(dialog) {
  if (openTouchLockedDialogs.size === 0) {
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
  }
  openTouchLockedDialogs.add(dialog);
}

/** @param {HTMLDialogElement} dialog */
function unlockTouch(dialog) {
  openTouchLockedDialogs.delete(dialog);
  if (openTouchLockedDialogs.size === 0) {
    document.removeEventListener('touchstart', onTouchStart, { capture: true });
    document.removeEventListener('touchmove', onTouchMove, { capture: true });
    touchAllowed = false;
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {'body' | 'touch' | null} */
  #lockMode = null;
  #settleTimer = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    if (this.#lockMode === 'touch' && this.refs.dialog) unlockTouch(this.refs.dialog);
    clearTimeout(this.#settleTimer);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #lockPage() {
    const { dialog } = this.refs;

    if (mobileQuery.matches) {
      lockTouch(dialog);
      this.#lockMode = 'touch';
      return;
    }

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    this.#lockMode = 'body';
  }

  #unlockPage() {
    const { dialog } = this.refs;

    if (this.#lockMode === 'touch') {
      unlockTouch(dialog);
    } else if (this.#lockMode === 'body') {
      document.body.style.width = '';
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
    }

    this.#lockMode = null;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    this.#lockPage();

    clearTimeout(this.#settleTimer);
    dialog.classList.remove('ps-dialog-settled');

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    this.#settleTimer = window.setTimeout(() => dialog.classList.add('ps-dialog-settled'), SETTLE_MS);

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#unlockPage();

    dialog.close();
    dialog.classList.remove('dialog-closing');
    clearTimeout(this.#settleTimer);
    dialog.classList.remove('ps-dialog-settled');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
